import * as protobuf from 'protobufjs/minimal';

import { Logger } from '@gitbook/util-debug';
import { assertNever } from '@gitbook/util-ts';

import { Block, Data, Document, Fragment, Inline, Leaf, Mark, Text } from '../models';
import * as proto from './slate';

interface SerializeOptions {
    /** Preserve keys on every nodes */
    preserveKeys?: boolean;
}

// protobufjs's default nesting limit (100) rejects documents ~49 blocks deep, as each block level costs
// two protobuf levels (Block → Node). 500 allows ~248 levels while staying well under the depth at
// which normalization overflows the stack (~1,000). The limits are protobufjs globals, so this applies to
// every protobuf message once this module is loaded.
const PROTOBUF_NESTING_LIMIT = 500;
// Keeps the logged base64 under Cloud Logging's 256 KB entry limit.
const MAX_LOGGED_BYTES = 64 * 1024;
// Spreading more bytes than this into one call can overflow the stack.
const BASE64_CHUNK_BYTES = 8 * 1024;

const logger = Logger('protobuf:serialization');

protobuf.Reader.recursionLimit = PROTOBUF_NESTING_LIMIT;
protobuf.util.recursionLimit = PROTOBUF_NESTING_LIMIT;

/**
 * Serialize a document to a protocol buffer.
 */
export function serializeToProtocolBuffer(
    document: Document,
    options: SerializeOptions = {}
): Uint8Array {
    const object = documentToObject(document, options);

    const verification = proto.Document.verify(object);
    if (verification) {
        throw new Error(`ProtoBufObject document is not valid: ${verification}`);
    }

    const message = proto.Document.create(object);
    return proto.Document.encode(message).finish();
}

/**
 * Deserialize a document from a protocol buffer.
 */
export function deserializeFromProtocolBuffer(buffer: Uint8Array): Document {
    let message: proto.Document;
    try {
        message = proto.Document.decode(buffer);
    } catch (error) {
        /**
         * In some cases the decode throws a RangeError: index out of range error. (RND-9788).
         * It's impossible to try to debug it without the buffer, hence why we're showing it in our
         * logs.
         * Looking at both https://github.com/protobufjs/protobuf.js/issues/691 and
         * https://github.com/protobufjs/protobuf.js/wiki/How-to-reverse-engineer-a-buffer-by-hand
         * we should log the buffer to understand what happens (whether the buffer is truncated,
         * contains bad data etc.)
         *
         * When this happens, start by decoding the base64 into a buffer
         *  const buf = Buffer.from(base64, 'base64');
         *
         * And ask Claude/Codex to walk through it.
         */
        const loggedBytes = buffer.subarray(0, MAX_LOGGED_BYTES);
        const truncated = loggedBytes.length < buffer.length;
        logger.error(
            `ProtoBufObject decode failed (${buffer.length} bytes): ${error} - base64${truncated ? ` (first ${loggedBytes.length} bytes)` : ''}: ${bytesToBase64(loggedBytes)}`
        );
        throw error;
    }

    const object = message.toJSON() as proto.IDocument;

    return objectToDocument(object);
}

function bytesToBase64(bytes: Uint8Array): string {
    let binary = '';
    for (let offset = 0; offset < bytes.length; offset += BASE64_CHUNK_BYTES) {
        binary += String.fromCharCode(...bytes.subarray(offset, offset + BASE64_CHUNK_BYTES));
    }
    return btoa(binary);
}

//
// To object
//

function documentToObject(document: Document, options: SerializeOptions): proto.IDocument {
    return {
        ...(options.preserveKeys ? { key: document.key } : {}),
        nodes: document.nodes.map((node) => blockToObject(node, options)).toArray(),
        data: dataToObject(document.data),
    };
}

function fragmentToObject(fragment: Fragment, options: SerializeOptions): proto.IFragment {
    return {
        nodes: fragment.nodes.toArray().map((child) => blockToObject(child, options)),
        ...(options?.preserveKeys && { key: fragment.key }),
        ...(fragment.fragment && { fragment: fragment.fragment }),
        ...(fragment.type && { type: fragment.type }),
    };
}

function blockToObject(block: Block, options: SerializeOptions): proto.IBlock {
    return {
        ...(options.preserveKeys ? { key: block.key } : {}),
        type: block.type,
        isVoid: block.isVoid,
        nodes: block.nodes.map((node) => nodeToObject(node, options)).toArray(),
        fragments: block.fragments
            .toValuesArray()
            .map((fragment) => fragmentToObject(fragment, options)),
        data: dataToObject(block.data),
    };
}

function inlineToObject(inline: Inline, options: SerializeOptions): proto.IInline {
    return {
        ...(options.preserveKeys ? { key: inline.key } : {}),
        type: inline.type,
        isVoid: inline.isVoid,
        nodes: inline.nodes.map((node) => nodeToObject(node, options)).toArray(),
        data: dataToObject(inline.data),
        fragments: inline.fragments
            .toValuesArray()
            .map((fragment) => fragmentToObject(fragment, options)),
    };
}

function textToObject(text: Text, options: SerializeOptions): proto.IText {
    return {
        ...(options.preserveKeys ? { key: text.key } : {}),
        leaves: text.leaves.map((leaf) => leafToObject(leaf)).toArray(),
    };
}

function leafToObject(leaf: Leaf): proto.ITextLeaf {
    return {
        text: leaf.text,
        marks: leaf.marks
            .map((mark) => ({
                type: mark.type,
                data: dataToObject(mark.data),
            }))
            .toArray(),
    };
}

function nodeToObject(node: Block | Text | Inline, options: SerializeOptions): proto.INode {
    switch (node.object) {
        case 'block':
            return { block: blockToObject(node, options) };
        case 'text':
            return { text: textToObject(node, options) };
        case 'inline':
            return { inline: inlineToObject(node, options) };
        default:
            assertNever(node);
    }
}

function dataToObject(data: Data): { [k: string]: string } {
    return data
        .filter((value) => value !== undefined)
        .map((value) => JSON.stringify(value))
        .toObject();
}

//
// From object
//

function objectToDocument(object: proto.IDocument): Document {
    return Document.create({
        key: object.key || undefined,
        nodes: (object.nodes || []).map((node) => objectToBlock(node)),
        data: objectToData(object.data || {}),
    });
}

function objectToBlock(object: proto.IBlock): Block {
    return Block.create({
        key: object.key || undefined,
        type: object.type!,
        isVoid: object.isVoid!,
        // @ts-ignore
        nodes: (object.nodes || []).map((node) => objectToNode(node)),
        fragments: (object.fragments || []).map((fragment) => objectToFragment(fragment)),
        data: objectToData(object.data || {}),
    });
}

function objectToInline(object: proto.IInline): Inline {
    return Inline.create({
        key: object.key || undefined,
        type: object.type!,
        isVoid: object.isVoid!,
        // @ts-ignore
        nodes: (object.nodes || []).map((node) => objectToNode(node)),
        fragments: (object.fragments || []).map((fragment) => objectToFragment(fragment)),
        data: objectToData(object.data || {}),
    });
}

function objectToText(object: proto.IText): Text {
    return Text.create({
        key: object.key || undefined,
        leaves: (object.leaves || []).map((leaf) => objectToLeaf(leaf)),
    });
}

function objectToNode(object: proto.INode): Block | Text | Inline {
    if (object.block) {
        return objectToBlock(object.block);
    }
    if (object.inline) {
        return objectToInline(object.inline);
    }
    if (object.text) {
        return objectToText(object.text);
    }
    throw new Error('Unrecognized node type');
}

function objectToLeaf(object: proto.ITextLeaf): Leaf {
    return Leaf.create({
        text: object.text!,
        marks: (object.marks || []).map((mark) =>
            Mark.create({
                type: mark.type!,
                data: objectToData(mark.data || {}),
            })
        ),
    });
}

function objectToFragment(object: proto.IFragment): Fragment {
    return Fragment.create({
        key: object.key || undefined,
        fragment: object.fragment || undefined,
        nodes: (object.nodes || []).map((node) => objectToBlock(node)),
        type: object.type || undefined,
    });
}

function objectToData(object: { [k: string]: string }): Data {
    const data = Data.create(
        Object.keys(object).map((key) => {
            const stringValue = object[key];
            const value = JSON.parse(stringValue);
            return [key, value];
        })
    );

    return data;
}
